import { createFileRoute, notFound } from "@tanstack/react-router";
import { MuranoLeksikonArticlePage } from "@/pages/MuranoLeksikonPage";
import { getLeksikonArticle } from "@/lib/murano-leksikon";
import { breadcrumbJsonLd, hreflangLinks, SITE_URL } from "@/lib/seo";

function buildHead(slug: string | undefined, locale: "da" | "en") {
  const article = slug ? getLeksikonArticle(slug) : undefined;
  if (!article) {
    return {
      meta: [
        {
          title:
            locale === "da"
              ? "Artiklen findes ikke — Farveladeland"
              : "Article not found — Farveladeland",
        },
        { name: "robots", content: "noindex" },
      ],
    };
  }
  const c = locale === "da" ? article.da : article.en;
  const path = `/murano/leksikon/${article.slug}`;
  const url =
    locale === "da" ? `${SITE_URL}${path}` : `${SITE_URL}/en${path}`;

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: c.title,
    description: c.excerpt,
    image: [article.cover],
    author: { "@type": "Organization", name: "Farveladeland", url: SITE_URL },
    publisher: {
      "@type": "Organization",
      name: "Farveladeland",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.ico` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    inLanguage: locale === "da" ? "da-DK" : "en",
    about: { "@type": "Thing", name: "Murano glass" },
  };

  const crumb = breadcrumbJsonLd([
    {
      name: locale === "da" ? "Forsiden" : "Home",
      url: locale === "da" ? SITE_URL : `${SITE_URL}/en`,
    },
    {
      name: locale === "da" ? "Murano-leksikon" : "Murano encyclopedia",
      url:
        locale === "da"
          ? `${SITE_URL}/murano/leksikon`
          : `${SITE_URL}/en/murano/leksikon`,
    },
    { name: c.title, url },
  ]);

  return {
    meta: [
      { title: `${c.title} — Farveladeland` },
      { name: "description", content: c.excerpt },
      { property: "og:title", content: c.title },
      { property: "og:description", content: c.excerpt },
      { property: "og:image", content: article.cover },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { property: "og:locale", content: locale === "da" ? "da_DK" : "en_US" },
      {
        property: "og:locale:alternate",
        content: locale === "da" ? "en_US" : "da_DK",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: article.cover },
    ],
    links: [
      { rel: "canonical", href: url },
      ...hreflangLinks({ path, locale }),
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleLd) },
      { type: "application/ld+json", children: JSON.stringify(crumb) },
    ],
  };
}

export const Route = createFileRoute("/murano/leksikon/$slug")({
  loader: ({ params }) => {
    const article = getLeksikonArticle(params.slug);
    if (!article) throw notFound();
    return { article };
  },
  head: ({ params }) => buildHead(params?.slug, "da"),
  component: () => {
    const { article } = Route.useLoaderData();
    return <MuranoLeksikonArticlePage article={article} locale="da" />;
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 pt-40 pb-32 text-center">
      <p className="eyebrow">404</p>
      <h1 className="mt-4 font-display text-5xl">Artiklen findes ikke</h1>
      <a
        href="/murano/leksikon"
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-xs font-medium uppercase tracking-[0.22em] text-background"
      >
        Til leksikonet →
      </a>
    </div>
  ),
});

export { buildHead as buildLeksikonHead };
